import React from 'react';
import { TimeValue } from '../date-range-picker/types';
import '../date-range-picker/TimeSelector.scss';
import { ReactComponent as ChevronDownIcon } from '../../images/chevron-down.svg';

interface CustomSelectProps {
    value: string;
    options: string[];
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

interface FormTimePickerProps {
    timeValue: TimeValue | null;
    onTimeChange: (time: TimeValue) => void;
    disabled?: boolean;
}

const CustomSelect: React.FC<CustomSelectProps> = ({ value, options, onChange }) => {
    return (
        <div className='custom-select-wrapper'>
            <select className='time-select' value={value} onChange={onChange}>
                {options.map((option) => (
                    <option key={option} value={option} className='time-option'>
                        {option}
                    </option>
                ))}
            </select>
            <span className='custom-select-icon'>
                <ChevronDownIcon />
            </span>
        </div>
    );
};

const FormTimePicker: React.FC<FormTimePickerProps> = ({
    timeValue,
    onTimeChange,
    disabled = false,
}) => {
    const hours = Array.from({ length: 12 }, (_, i) => String(i === 0 ? 12 : i).padStart(2, '0'));
    const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
    const seconds = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

    // Default time value
    const defaultTime: TimeValue = {
        hours: '12',
        minutes: '00',
        seconds: '00',
        period: 'AM'
    };

    const currentTime = timeValue || defaultTime;

    const handleHoursChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        onTimeChange({ ...currentTime, hours: e.target.value });
    };

    const handleMinutesChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        onTimeChange({ ...currentTime, minutes: e.target.value });
    };

    const handleSecondsChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        onTimeChange({ ...currentTime, seconds: e.target.value });
    };

    const handlePeriodChange = (period: 'AM' | 'PM') => {
        onTimeChange({ ...currentTime, period });
    };

    if (disabled) {
        return (
            <div>
                <div className='time-selector-container'>
                    <div className='time-section'>
                        <div className='time-input-wrapper'>
                            <div className='time-input-container'>
                                <div className='time-input-inner'>
                                    <input
                                        type="text"
                                        value={`${currentTime.hours}:${currentTime.minutes}:${currentTime.seconds} ${currentTime.period}`}
                                        disabled={true}
                                        className='field-input disabled'
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='time-selector  p-1'>
            <div className='time-selector-container'>
                <div className='time-section'>
                    <div className='time-input-wrapper'>
                        <div className='time-input-container'>
                            <div className='time-input-inner'>
                                <CustomSelect value={currentTime.hours} options={hours} onChange={handleHoursChange} />
                            </div>
                        </div>
                    </div>
                    <span className='time-separator'>:</span>
                    <div className='time-input-wrapper'>
                        <div className='time-input-container'>
                            <div className='time-input-inner'>
                                <CustomSelect value={currentTime.minutes} options={minutes} onChange={handleMinutesChange} />
                            </div>
                        </div>
                    </div>
                    <span className='time-separator'>:</span>
                    <div className='time-input-wrapper'>
                        <div className='time-input-container'>
                            <div className='time-input-inner'>
                                <CustomSelect value={currentTime.seconds} options={seconds} onChange={handleSecondsChange} />
                            </div>
                        </div>
                    </div>
                    <div className='period-toggle'>
                        <button
                            className={`period-button period-button-left ${currentTime.period === 'AM' ? 'period-active' : ''}`}
                            onClick={() => handlePeriodChange('AM')}
                        >
                            <span className={`period-text ${currentTime.period === 'AM' ? 'period-text-active' : ''}`}>AM</span>
                        </button>
                        <button
                            className={`period-button period-button-right ${currentTime.period === 'PM' ? 'period-active' : ''}`}
                            onClick={() => handlePeriodChange('PM')}
                        >
                            <span className={`period-text ${currentTime.period === 'PM' ? 'period-text-active' : ''}`}>PM</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FormTimePicker;
